// @flow
import * as actionTypes from '../constants/action_types'

export type StatisticsType = {
    [key: string]: {
        min: number,
        max: number,
    },
}

export type ActionsType = {
    getUserLayerClassifications: Function,
    getPublicServices: Function,
    onUserLayersAdd: Function,
    onUserLayerDelete: Function,
    onUserLayerGetAttributes: Function,
    onUserLayersPropsChange: Function,
    onUserLayersSort: Function,
    onUserLayersZoom: Function,
}

export type UserLayerClassificationType = {
    bordercolor?: string,
    borderopacity: number,
    bordersize: number,
    color: string,
    expression: string,
    hatchsize: string,
    iconpath?: string,
    label: string,
    opacity: number,
    outlinecolor: string,
    rangemax?: number,
    rangemin?: number,
    size: number,
    // Iconn or pattern
    symbol: string,
    // Icon type of pattern type
    symbolType: string,
}

export type UserLayerType = {
    classifications: UserLayerClassificationType[],
    extent: ExtentType,
    filepath: string,
    name: string,
    opacity: number,
    randomColors: boolean,
    schema: {
        geometry: string,
        properties: {}
    },
    visible: boolean,
}

export type UserMapsourceType = {
    // Cprresponds to a single collection of layers represented by a single mapfile.
    layers: UserLayerType[],
    label: string,
    mapfile: string,
    name: string,
    tags: string[],
    type: string,
}

type MapsourceActionType = {
    type: string,
    id: string,
    layerName?: string,
    layerNames?: string[],
    mapsources?: Array<UserMapsourceType>,
    mapsourceName?: string,
    newProps?: any,
    ordering?: Array<UserMapsourceType>,
    state?: any,
    stats?: StatisticsType,
    userlayerProps?: UserLayerType,
}

export type MapsourcesType = {
    // True if the default mapsource has been altered. Currently only used for mapserver
    dirty: boolean,
    mapfile: string,
    mapsources: UserMapsourceType[],
}

const initialState = {
    dirty: false,
    mapsources: [],
}

/**
 * Return new mapsource and mutate the layer so it can be directly modified
 * @param state
 * @param action
 * @returns {{newstate: {baseLayer: string, extent?: ExtentType, mapView: ViewType, mapsources: Array<UserMapsourceType>}, imapsource: number, mapsource: {layers: UserLayerType[], mapfile: string, label: string, name: string}, ilayer: number, layer: *}}
 */
const getLayerForMapsource = (state: MapsourcesType, mapsourceName: string, layerName: string) => {
    const imapsource = state.mapsources.findIndex(f => f.name === mapsourceName)
    const mapsource = state.mapsources[imapsource]
    // Locate the layer
    const ilayer = mapsource.layers.findIndex(l => l.name === layerName)
    const layer = { ...mapsource.layers[ilayer] }
    const newMapsource = { ...mapsource, layers: [...mapsource.layers.slice(0, ilayer), layer, ...mapsource.layers.slice(ilayer + 1)] }
    const newstate = {
        ...state,
        mapsources: [
            ...state.mapsources.slice(0, imapsource),
            newMapsource,
            ...state.mapsources.slice(imapsource + 1),
        ],
    }
    return { newstate, imapsource, mapsource: newMapsource, ilayer, layer: newMapsource.layers[ilayer] }
}

export default function mapsources(state: MapsourcesType = initialState, action: MapsourceActionType) {
    let newstate = state

    // Locate the mapsource and layer
    function getMapsourceAndLayerIndex() {
        const imapsource = state.mapsources.findIndex(f => f.name === action.mapsourceName)
        const mapsource = state.mapsources[imapsource]
        // Locate the layer
        const ilayer = mapsource.layers.findIndex(l => l.name === action.layerName)
        const layer = mapsource.layers[ilayer]
        return { imapsource, mapsource, ilayer, layer }
    }

    if (action.type === actionTypes.SET_FETCHED_STATE) {
        newstate = {
            ...state,
            dirty: true,
            mapsources: action.state.mapsources,
        }
    } else if (action.type === actionTypes.PUBLIC_DATA_PROPS_CHANGE) {
        // Public service definitions are stored in public_sites reducer, but service mapsource props are stored here.
        const mapsourceName = `${action.siteName}_${action.serviceName}`.replace('/', '__')
        const imapsource = state.mapsources.findIndex(f => f.name === mapsourceName)
        if (imapsource < 0) console.error(`No mapsource for public service ${mapsourceName}`)
        const mapsource = state.mapsources[imapsource]
        const newMapsource = { ...mapsource, ...action.serviceProps }

        newstate = {
            ...state,
            dirty: true,
            mapsources: [
                ...state.mapsources.slice(0, imapsource),
                newMapsource,
                ...state.mapsources.slice(imapsource + 1),
            ],
        }
    } else if (action.type === actionTypes.UPDATE_FIELD_STATS) {
        // Locate the mapsource
        const ret = getLayerForMapsource(state, action.mapsourceName, action.layerName)
        newstate = ret.newstate
        Object.assign(newstate.mapsources[ret.imapsource].layers[ret.ilayer].statistics, action.stats)
    } else if (action.type === actionTypes.UPDATE_MAPSOURCE_LAYERS) {
        newstate = state
        let layerNames = action.layerNames
        if (!Array.isArray(action.layerNames)) {
            layerNames = [action.layerNames]
        }
        layerNames.forEach((layerName) => {
            const ret = getLayerForMapsource(newstate, action.mapsourceName, layerName)
            newstate = ret.newstate
            // Update layer state
            Object.assign(newstate.mapsources[ret.imapsource].layers[ret.ilayer], action.newProps)
        })
    } else if (action.type === actionTypes.UPDATE_MAPSOURCE_ORDER) {
        // Reorder the mapsources to match the order
        const mapsourcesFromNames = action.mapsourceNames.map(name => state.mapsources.find(m => m.name === name))
        newstate = {
            ...state,
            dirty: true,
            mapsources: mapsourcesFromNames,
        }
    } else if (action.type === actionTypes.USER_LAYER_DELETE) {
        const { imapsource, mapsource, ilayer } = getMapsourceAndLayerIndex()
        const newMapsource = {
            ...mapsource,
            layers: [...mapsource.layers.slice(0, ilayer), ...mapsource.layers.slice(ilayer + 1)],
        }
        newstate = {
            ...state,
            dirty: true,
            mapsources: [
                ...state.mapsources.slice(0, imapsource),
                newMapsource,
                ...state.mapsources.slice(imapsource + 1),
            ],
        }
    } else if (action.type === actionTypes.USER_LAYERS_DELETE) {
        newstate = {
            ...state,
            dirty: true,
            layers: state.layers.filter(layerToDelete => action.layers.find(l => l.name === layerToDelete.name) === null),
        }
    } else if (action.type === actionTypes.USER_LAYERS_SET) {
        newstate = {
            ...state,
            dirty: true,
            mapsources: action.mapsources,
        }
    } else if (action.type === actionTypes.USER_LAYERS_SORT) {
        const imapsource = state.mapsources.findIndex(f => f.name === action.mapsourceName)
        const mapsource = state.mapsources[imapsource]
        // rearrange the layers
        const layers = action.layerNames.map(n => mapsource.layers.find(l => l.name === n))
        const newMapsource = { ...mapsource, layers }
        newstate = {
            ...state,
            dirty: true,
            mapsources: [
                ...state.mapsources.slice(0, imapsource),
                newMapsource,
                ...state.mapsources.slice(imapsource + 1),
            ],
        }
    } else if (action.type === actionTypes.USER_LAYER_UPDATE) {
        // User is updating a single layer
        const imapsource = state.mapsources.findIndex(f => f.name === action.mapsourceName)
        const mapsource = state.mapsources[imapsource]
        const ilayer = mapsource.layers.findIndex(l => l.name === action.layerName)
        const newMapsource = { ...mapsource, layers: [...mapsource.layers.slice(0, ilayer), action.newProps, ...mapsource.layers.slice(ilayer + 1)] }
        newstate = {
            ...state,
            dirty: true,
            mapsources: [
                ...state.mapsources.slice(0, imapsource),
                newMapsource,
                ...state.mapsources.slice(imapsource + 1),
            ],
        }
    } else if (action.type === actionTypes.USER_LAYERS_ZOOM) {
        if (action.layerNames.length > 1) {
            console.warn('Only zooming to first layer of list')
        }
        const imapsource = state.mapsources.findIndex(f => f.name === action.mapsourceName)
        const mapsource = state.mapsources[imapsource]
        // Locate the layer
        const layer = mapsource.layers.find(l => l.name === action.layerNames[0])
        newstate = {
            ...state,
            extent: layer.bbox_3857,
        }
    }

    return newstate
}
